import structuredClone from "@ungap/structured-clone";
import { useEffect } from "react";

import type { DataPoint } from "../schemas";
import { useChannelsStore } from "../store/channels-store";
import { useDevicesStore } from "../store/devices-store";
import type {
  DeviceChannels,
  FumehoodChannelsKeys,
  OperationMode,
} from "../view-model";
import { useActiveRoom } from "./use-rooms";
import { useLADSValues } from "./use-websocket-lads";

// LADS uses WebSocket connections for real-time data

// Removed laboperator-based channel values function - LADS uses WebSocket

export const patchChannelsWithValues = (
  channels: DeviceChannels,
  dataPoints?: DataPoint[]
) => {
  const updatedChannels = structuredClone(channels);

  Object.entries(channels).map(([key, channel]) => {
    if (dataPoints)
      for (const dataPoint of dataPoints) {
        if (dataPoint[3] == channel.id) {
          // since we map the channels they cant be undefined
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          updatedChannels[key as FumehoodChannelsKeys]!.current = {
            value: (dataPoint[1] === null
              ? dataPoint[2]
              : dataPoint[1]) as string as OperationMode,
            timestamp: new Date(dataPoint[0]),
          };
        }
        if (dataPoint[3] == channel.targetValue?.channel) {
          // since we map the channels they cant be undefined
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          updatedChannels[key as FumehoodChannelsKeys]!.targetValue = {
            channel: channel.targetValue.channel,
            value: dataPoint[1] as string as OperationMode,
            timestamp: new Date(dataPoint[0]),
          };
        }
      }
  });
  return updatedChannels;
};

// Removed laboperator-based data points function - LADS uses WebSocket

export function useChannelValuesForDevice(
  unitId: string,
  channels: DeviceChannels,
  parentDeviceId: string
) {
  // Scoped key ensures uniqueness when multiple devices have units with the same name
  const scopedUnitId = `${parentDeviceId}/${unitId}`;

  // Subscribe to store state directly for proper re-renders
  const storedChannels = useChannelsStore(
    (state) => state.channelValues[scopedUnitId]?.channels
  );
  const isLoading = useChannelsStore(
    (state) => state.channelValues[scopedUnitId]?.isLoading ?? false
  );
  const error = useChannelsStore(
    (state) => state.channelValues[scopedUnitId]?.error ?? null
  );
  const { setChannelError, setChannelLoading, setChannelValues } =
    useChannelsStore();

  // Subscribe to the DEVICE's own room so live value updates keep flowing after
  // a room move — the backend re-routes the device's WS values to the new room.
  // Fall back to the active room until the device is in the store.
  const deviceRoom = useDevicesStore(
    (state) => state.devices[parentDeviceId]?.location?.room
  );
  const { activeRoom } = useActiveRoom();
  const roomId = deviceRoom || activeRoom?.id || "";

  // LADS WebSocket for real-time value updates
  const { isConnected: isWebSocketConnected } = useLADSValues({
    roomId,
    unitId,
    parentDeviceId,
    channels,
    enabled: true,
  });

  // Initialize channels in store only if not already present
  // This prevents infinite loops when the store update triggers re-renders
  useEffect(() => {
    if (channels && !storedChannels) {
      setChannelValues(scopedUnitId, channels);
      setChannelLoading(scopedUnitId, false);
      setChannelError(scopedUnitId, null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopedUnitId]);

  return {
    channels: storedChannels,
    error,
    isLoading,
    mutate: null,
    isWebSocketConnected,
  };
}
